import {
  CultivarSectionRefs,
  CultivationDataValues,
  CultivationFieldSectionData,
  FieldDataValues
} from "../types/cultivarForm.types";

const mapFertilizerList = (items: any[] = []) =>
  items.map(item => ({
    id: item.id,
    fertilizerQuantity: item.fertilizerQuantity
  }));

const mapPesticideList = (items: any[] = []) =>
  items.map(item => ({
    id: item.id,
    pesticideQuantity: item.pesticideQuantity
  }));

export const resolveCreatedFarmGateId = (response: unknown): string | undefined => {
  if (!response || typeof response !== "object") return undefined;

  const payload = response as Record<string, unknown>;
  const nestedData = payload.data as Record<string, unknown> | undefined;
  const id = payload._id ?? nestedData?._id ?? payload.id ?? nestedData?.id;

  return id != null ? String(id) : undefined;
};

export const getCultivationDataFallback = (
  currentFormData: any
): CultivationDataValues | null => {
  if (!currentFormData) return null;

  return {
    dataType: currentFormData.dataType,
    produceType: currentFormData.produceType,
    farmingType: currentFormData.farmingType,
    farmingField: currentFormData.farmingField,
    region: currentFormData.region,
    latitude: currentFormData.latitude,
    longitude: currentFormData.longitude,
    produceCultivar: currentFormData.produceCultivar
  };
};

export const getFieldDataFallback = (currentFormData: any): FieldDataValues | null => {
  const cultivationField = currentFormData?.cultivationField;
  if (!cultivationField) return null;

  return {
    yield: cultivationField.yield,
    growthArea: cultivationField.growthArea,
    harvestYear: cultivationField.harvestYear,
    harvestSeason: cultivationField.harvestSeason
  };
};

export const collectCultivationFieldSectionData = (
  sectionRefs: CultivarSectionRefs,
  currentFormData: any
): CultivationFieldSectionData => {
  const cultivationField = currentFormData?.cultivationField ?? {};

  const fertilizerTableList =
    sectionRefs.fertilizer.current?.getFretilizerData?.() ??
    cultivationField.fertilizerList ??
    [];
  const pesticideTableList =
    sectionRefs.pesticide.current?.getPesticideData?.() ?? cultivationField.pesticideList ?? [];
  const irrigations =
    sectionRefs.irrigation.current?.getIrrigationData?.() ?? cultivationField.irrigations ?? [];
  const fuelUse =
    sectionRefs.fuelUse.current?.getFuelUse?.() ??
    cultivationField.fuelUse ??
    currentFormData?.fuelUse ??
    [];
  const landUsage =
    sectionRefs.landUse.current?.getLandUseData?.() ??
    cultivationField.landUsage ??
    currentFormData?.landUsage ??
    [];

  return {
    fertilizerList: mapFertilizerList(fertilizerTableList),
    pesticideList: mapPesticideList(pesticideTableList),
    irrigations,
    fuelUse,
    landUsage
  };
};

export const resolveCultivationData = async (
  sectionRefs: CultivarSectionRefs,
  currentFormData: any
): Promise<CultivationDataValues | null> => {
  const fromRef = await sectionRefs.cultivation.current?.getCultivationData?.();
  return fromRef ?? getCultivationDataFallback(currentFormData);
};

export const resolveFieldData = async (
  sectionRefs: CultivarSectionRefs,
  currentFormData: any
): Promise<FieldDataValues | null> => {
  const fromRef = await sectionRefs.field.current?.getFieldData?.();
  return fromRef ?? getFieldDataFallback(currentFormData);
};

export const buildCultivationFieldPayload = (
  savedFarmGateId: string,
  mergedValues: Record<string, any>,
  fieldData: FieldDataValues | null,
  sectionData: CultivationFieldSectionData,
  currentFormData: any
) => ({
  farmGateId: savedFarmGateId,
  yield: mergedValues?.yield ?? fieldData?.yield,
  growthArea: mergedValues?.growthArea ?? fieldData?.growthArea,
  harvestYear: mergedValues?.harvestYear ?? fieldData?.harvestYear,
  harvestSeason: mergedValues?.harvestSeason ?? fieldData?.harvestSeason,
  produceCultivar: mergedValues?.produceCultivar ?? currentFormData?.produceCultivar,
  ...sectionData
});

export const buildCultivationPayload = ({
  formValues,
  supplierId,
  ingredientId,
  ownerId,
  sourceId,
  farmGateId,
  currentFormData,
  cultivationData,
  fieldData,
  sectionData,
  landUsageChangeStatus,
  userCompletedStage
}: {
  formValues: Record<string, any>;
  supplierId: string;
  ingredientId: string;
  ownerId: string;
  sourceId: string;
  farmGateId?: string;
  currentFormData: any;
  cultivationData: CultivationDataValues;
  fieldData: FieldDataValues;
  sectionData: CultivationFieldSectionData;
  landUsageChangeStatus: string;
  userCompletedStage: string;
}) => ({
  ...(farmGateId ? { farmGateId } : {}),
  ...formValues,
  supplierId,
  ingredientId,
  ownerId,
  sourceId,
  processing: currentFormData?.processing || [],
  packaging: currentFormData?.packaging || [],
  internalTransportation: currentFormData?.internalTransportation || [],
  userCompletedStage,
  landUsageChange: landUsageChangeStatus,
  landUsage: sectionData.landUsage,
  ...cultivationData,
  ...fieldData
});
